import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import cx from 'classnames'
import styles from './RulesCard.module.scss'
import { Col, Container, Row } from 'react-grid-system'
import { Heading, Paragraph } from '~elements'
import { useWindowSize } from 'utils/hooks'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { AssetType, RichTextType } from '~types'

interface RulesCardProps {
  headline: string
  rulesCardArray: Array<{
    description?: RichTextType
    image: AssetType
  }>
}

interface SliderInterface {
  slickNext: () => void
  slickPrev: () => void
}

const RulesCard = ({ rulesCardArray, headline }: RulesCardProps) => {
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const { isBreakpoint: isBelowBreakpointSm } = useWindowSize('sm', '<')
  const [touchX, setTouchX] = useState<number | null>(null)
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null)
  const [activeTileIndex, setActiveTileIndex] = useState<number>(0)
  const sliderRef = useRef<SliderInterface | null>(null)
  const limitedItems = rulesCardArray.slice(0, 4)
  const [activeImage, setActiveImage] = useState<string>('')
  const [activeDescription, setActiveDescription] = useState<{ html: string | null }>({ html: '' })

  const swipeDistanceLimit = 50

  useEffect(() => {
    if (rulesCardArray[activeTileIndex]?.image?.url) {
      setActiveImage(rulesCardArray[activeTileIndex]['image']?.url)
    }
    if (rulesCardArray[activeTileIndex]?.description) {
      setActiveDescription(rulesCardArray[activeTileIndex]?.description ?? { html: '' })
    }
  }, [activeTileIndex])

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchX(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchX && rulesCardArray.length > 1) {
      const xPosition = e.touches[0].clientX
      const distance = Math.abs(touchX - xPosition)
      if (distance > swipeDistanceLimit) {
        if (touchX > xPosition) {
          setSwipeDirection('left')
        } else {
          setSwipeDirection('right')
        }
      }
    }
  }

  const handleTouchEnd = () => {
    if (touchX && rulesCardArray.length > 1) {
      if (swipeDirection === 'right') {
        prevSlideHandler()
      } else if (swipeDirection === 'left') {
        nextSlideHandler()
      }
    }
    setTouchX(null)
    setSwipeDirection(null)
  }

  const prevSlideHandler = () => {
    const newIndex = activeTileIndex === 0 ? limitedItems.length - 1 : activeTileIndex - 1
    setActiveTileIndex(newIndex)
    sliderRef?.current?.slickPrev()
  }

  const nextSlideHandler = () => {
    const newIndex = activeTileIndex === limitedItems.length - 1 ? 0 : activeTileIndex + 1
    setActiveTileIndex(newIndex)
    sliderRef?.current?.slickNext()
  }

  const ImageComponent = (props: { src: string }) => {
    const { src } = props || {}
    return src ? (
      <Image
        src={src}
        alt={''}
        className={styles['tile-image']}
        quality={NEXT_IMAGE_QUALITY}
        width={600}
        height={370}
      />
    ) : null
  }

  return (
    <Container className={styles['container']}>
      <Row>
        <Col>
          <div className={styles['headline-container']}>
            {headline && (
              <Heading level={1} justify={isBelowBreakpointSm ? 'center' : 'left'}>
                <span className={styles['headline']}>{headline}</span>
              </Heading>
            )}
          </div>
          {isBelowBreakpointSm ? (
            <>
              <div
                className={cx([styles['rules-card-container']])}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
              >
                <div className={styles['image-container']}>
                  <ImageComponent src={activeImage} />
                  <div className={styles['description-container']}>
                    {
                      <Paragraph
                        fontSize={'sm'}
                        text={activeDescription}
                        justify={isBelowBreakpointMd ? 'center' : 'left'}
                      />
                    }
                  </div>
                </div>
              </div>
              <div className={styles['carousel-container']}>
                <div className={styles['indicator-position']}>
                  {limitedItems.map((listItem, index) => (
                    <span
                      key={`indicator-${index}`}
                      className={`${styles['indicator']} ${
                        index === activeTileIndex ? styles['active-indicator'] : ''
                      }`}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className={cx([styles['rules-card-container']])}>
              {rulesCardArray.map((item, index) => (
                <div className={styles['rules-card']} key={index}>
                  <div className={styles['image-container']}>
                    <ImageComponent src={item?.image?.url} />
                  </div>
                  <div className={styles['description-container']}>
                    {item?.description && (
                      <Paragraph
                        fontSize={'xl'}
                        text={item.description}
                        justify={isBelowBreakpointMd ? 'center' : 'left'}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export default RulesCard
